import React, { useMemo, useState } from 'react';
import { Card, Text, Group, Table, Pagination } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { TableProps } from '../../types/metrics';
import { apiService } from '../../services/apiClient';
import './TableRenderer.css';
import '../ui/MetricTable.css';
import { BmBadge, isCydRateColumn } from '../ui/BmBadge';
import { MetricTitle } from '../ui/MetricTitle';
import { MetricTableToolbar } from '../ui/MetricTableToolbar';
import { getCydRateGrayBadgeStyle, getCydRateRange } from '../../utils/cydRateBadgeStyle';
import { formatTableHeaderLabel, getTableColumnClass } from '../../utils/tableColumnAlign';

const TableRenderer: React.FC<TableProps> = ({ 
  config, 
  data, 
  loading, 
  error,
  columns = [],
  pageSize = 10,
  storeId,
  startDate,
  endDate
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [cydRateMode, setCydRateMode] = useState<'historical' | 'range'>('range');
  const [minOrders, setMinOrders] = useState(3);

  const isTopCustomersCydRate = config.id === 'top-customers-cyd-rate';
  React.useEffect(() => {
    if (!isTopCustomersCydRate) {
      setDebouncedSearch('');
      return;
    }
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm, isTopCustomersCydRate]);
  const { data: topCustomersData, isLoading: topCustomersLoading, error: topCustomersError } = useQuery({
    queryKey: ['top-customers-cyd-rate', storeId, cydRateMode, startDate, endDate, minOrders],
    queryFn: async () => {
      const endpoint = `/api/metrics/${storeId}/top-customers-cyd-rate`;
      const url = new URL(endpoint, process.env.REACT_APP_API_URL || '');
      if (cydRateMode === 'range' && startDate && endDate) {
        url.searchParams.set('startDate', startDate);
        url.searchParams.set('endDate', endDate);
      }
      url.searchParams.set('minOrders', String(minOrders));
      const response = await apiService.get(url.pathname + url.search);
      return response.data;
    },
    enabled: isTopCustomersCydRate && !!storeId && !debouncedSearch && (cydRateMode === 'historical' ? true : !!startDate && !!endDate),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  const { data: topCustomersSearchData, isLoading: topCustomersSearchLoading, error: topCustomersSearchError } = useQuery({
    queryKey: ['top-customers-cyd-rate-search', storeId, cydRateMode, startDate, endDate, debouncedSearch, minOrders],
    queryFn: async () => {
      const endpoint = `/api/metrics/${storeId}/top-customers-cyd-rate-search`;
      const url = new URL(endpoint, process.env.REACT_APP_API_URL || '');
      if (cydRateMode === 'range' && startDate && endDate) {
        url.searchParams.set('startDate', startDate);
        url.searchParams.set('endDate', endDate);
      }
      url.searchParams.set('query', debouncedSearch);
      url.searchParams.set('minOrders', String(minOrders));
      const response = await apiService.get(url.pathname + url.search);
      return response.data;
    },
    enabled: isTopCustomersCydRate && !!storeId && !!debouncedSearch && (cydRateMode === 'historical' ? true : !!startDate && !!endDate),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  const getTableData = () => {
    const sourceData = isTopCustomersCydRate
      ? (debouncedSearch ? topCustomersSearchData : topCustomersData)
      : data;
    if (!sourceData) return [];
    
    // Si data es un array, usarlo directamente
    if (Array.isArray(sourceData)) return sourceData;
    
    // Si data tiene una propiedad que contiene el array
    if ((sourceData as any).data) return (sourceData as any).data;
    if ((sourceData as any).items) return (sourceData as any).items;
    if ((sourceData as any).products) return (sourceData as any).products;
    if ((sourceData as any).orders) return (sourceData as any).orders;
    
    return [];
  };

  const tableData = getTableData();
  
  // Filtrar por término de búsqueda
  const filteredData = tableData.filter((item: any) => {
    if (!searchTerm) return true;
    return Object.values(item).some(value => 
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = filteredData.slice(startIndex, endIndex);
  const cydRateRange = useMemo(
    () => (isTopCustomersCydRate ? getCydRateRange(filteredData) : null),
    [filteredData, isTopCustomersCydRate]
  );

  const getColumnHeaders = () => {
    if (config.id === 'top-customers-cyd-rate') {
      return ['customer_email', 'customer_name', 'total_orders', 'total_units_sold', 'total_cyd_requests', 'cyd_rate'];
    }
    if (currentData.length === 0) return [];
    
    const firstItem = currentData[0];
    return Object.keys(firstItem).filter(key => 
      !key.startsWith('_') && 
      key !== 'id' && 
      typeof firstItem[key] !== 'object'
    );
  };

  // Mapeo de nombres de columnas a español
  const columnNameMapping: Record<string, string> = {
    'product_name': 'Producto',
    'product_sku': 'SKU',
    'product_category': 'Categoría',
    'product_brand': 'Marca',
    'quantity_sold': 'Cantidad',
    'unit_price': 'Precio Unit.',
    'total_line_amount': 'Total',
    'order_id': 'ID de Orden',
    'order_number': 'Número de Orden',
    'order_date': 'Fecha de Orden',
    'order_status': 'Estado',
    'customer_email': 'Email del Cliente',
    'customer_name': 'Nombre del Cliente',
    'customer_identification': 'Identificación',
    'store_id': 'ID de Tienda',
    'store_name': 'Nombre de Tienda',
    'created_at': 'Fecha de Creación',
    'updated_at': 'Fecha de Actualización',
    'name': 'Nombre',
    'value': 'Cantidad',
    'total_orders': 'Total Órdenes',
    'total_units_sold': 'Uds. Vendidas',
    'total_cyd_requests': 'Uds. CyD',
    'total_cyd': 'Total CyD',
    'cyd_rate': 'Tasa CyD (%)',
    'total_amount': 'Total ($)',
    'avg_order_value': 'Promedio Orden',
    'variant_name': 'Variante',
    'boris_action': 'Acción',
    'boris_reason': 'Motivo',
    'boris_request_date': 'Fecha Solicitud',
    'boris_completion_date': 'Fecha Cierre',
    'resolution_days': 'Días Resolución',
    'status': 'Estado',
    'courier_method': 'Método Envío',
    'courier_company': 'Empresa Logística'
  };

  const formatCellValue = (value: any, columnName: string) => {
    if (value === null || value === undefined) return '-';
    const isTopCustomersCydRate = config.id === 'top-customers-cyd-rate';
    const unitColumns = new Set(['total_units_sold', 'total_cyd_requests', 'total_orders']);
    
    if (isTopCustomersCydRate && unitColumns.has(columnName)) {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return value.toString();
      return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(numValue);
    }

    // Formatear como moneda
    if (columnName.includes('price') || columnName.includes('amount') || columnName.includes('total')) {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return value.toString();
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS'
      }).format(numValue);
    }
    
    // Formatear como número
    if (columnName.includes('quantity') || columnName.includes('count') || columnName.includes('id') || columnName.includes('orders')) {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return value.toString();
      return new Intl.NumberFormat('es-AR').format(numValue);
    }
    
    if (isCydRateColumn(columnName) || (columnName.includes('rate') && config.id.includes('cyd'))) {
      const numValue = parseFloat(value);
      const label = isNaN(numValue) ? '0%' : `${numValue.toFixed(2)}%`;

      if (isTopCustomersCydRate && cydRateRange) {
        return (
          <BmBadge
            variant="neutral"
            style={getCydRateGrayBadgeStyle(numValue, cydRateRange.min, cydRateRange.max)}
          >
            {label}
          </BmBadge>
        );
      }

      return <BmBadge variant="cyd">{label}</BmBadge>;
    }
    
    // Formatear días de resolución
    if (columnName === 'resolution_days') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return '0';
      return `${numValue} días`;
    }
    
    // Formatear fechas
    if (columnName.includes('date') || columnName.includes('created_at') || columnName.includes('updated_at')) {
      try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return value.toString();
        return date.toLocaleDateString('es-AR', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch {
        return value.toString();
      }
    }
    
    // Formatear estado con colores
    if (columnName === 'status') {
      if (value === 'Cerrada') {
        return <BmBadge variant="insight">{value}</BmBadge>;
      }
      if (value === 'Abierta') {
        return <BmBadge variant="warning">{value}</BmBadge>;
      }
    }
    
    return value.toString();
  };

  const headers = getColumnHeaders();

  if (loading || topCustomersLoading || topCustomersSearchLoading) {
    return (
      <Card className="metric-panel" shadow="sm" padding="md" radius="md" withBorder>
        <MetricTitle title={config.title} metricId={config.id} mb="xs" />
        <div className="table-loading">
          <div className="loading-spinner"></div>
          <span>Cargando tabla...</span>
        </div>
      </Card>
    );
  }

  if (error || topCustomersError || topCustomersSearchError) {
    return (
      <Card className="metric-panel" shadow="sm" padding="md" radius="md" withBorder>
        <MetricTitle title={config.title} metricId={config.id} mb="xs" />
        <Text size="sm" c="negative">Error: {error || (topCustomersError as Error)?.message || (topCustomersSearchError as Error)?.message}</Text>
      </Card>
    );
  }

  return (
    <Card className="metric-panel" shadow="sm" padding="md" radius="md" withBorder>
      <MetricTitle
        title={config.title}
        metricId={config.id}
        mb="xs"
        description={config.description}
      />

      <MetricTableToolbar
        tabs={
          isTopCustomersCydRate
            ? [
                { value: 'historical', label: 'Histórico' },
                { value: 'range', label: 'Rango' },
              ]
            : undefined
        }
        activeTab={cydRateMode}
        onTabChange={(value) => setCydRateMode(value as 'historical' | 'range')}
        tabAriaLabel="Modo de cálculo CyD"
        showMin={isTopCustomersCydRate}
        minValue={minOrders}
        onMinChange={setMinOrders}
        minLabel="Mín. órdenes"
        showSearch
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Buscar en la tabla..."
      />
      
      <div className="table-content">
        {tableData.length === 0 ? (
          <div className="table-no-data">
            <p>No hay datos disponibles</p>
          </div>
        ) : (
          <>
            <Table className="metric-table">
              <Table.Thead>
                <Table.Tr>
                  {headers.map(header => (
                    <Table.Th key={header} className={getTableColumnClass(header)}>
                      {formatTableHeaderLabel(
                        header,
                        columnNameMapping[header] || header.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                      )}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {currentData.map((row: any, index: number) => (
                  <Table.Tr key={index}>
                    {headers.map(header => (
                      <Table.Td key={header} className={getTableColumnClass(header)}>
                        {formatCellValue(row[header], header)}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            
            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination
                  total={totalPages}
                  value={currentPage}
                  onChange={setCurrentPage}
                />
              </Group>
            )}
            
            <Text size="xs" c="dimmed" ta="center" mt="xs">
              Mostrando {startIndex + 1}-{Math.min(endIndex, filteredData.length)} de {filteredData.length} registros
            </Text>
          </>
        )}
      </div>
    </Card>
  );
};

export default TableRenderer;
